import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Navigate } from 'react-router-dom';
import { GoogleLogin } from '@react-oauth/google';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../hooks/useAuth';
import EmailOTPLoginForm from './EmailOTPLoginForm';
import PhoneOTPLoginForm from './PhoneLoginForm';
import { REGION_CONFIG, getApiBaseUrl } from '../../config/region';
import {
  isInLineBrowser, getLiffId, initLiff,
  isLiffLoggedIn, getLiffAccessToken, liffLogin,
} from '../../services/liffService';

// 2026-06-21 — Zalo OAuth temporarily disabled (callback wasn't working).
// 2026-07-14 — RE-ENABLED (EV-1243): root cause was the Zalo app Home URL still pointed at
// thai.honestlee.app after we moved to vn.honestlee.app; domain re-verified on vn (verifier
// file + meta tag added to the consumer). Flip back to false if the flow errors for users.
const ZALO_LOGIN_ENABLED = true;
// Phone-number OTP (SMS). Disabled 2026-06-21 — SMS OTP isn't reliably delivering
// (e.g. UK/intl numbers failed). Hidden from the UI until the SMS provider works.
// Set true to re-enable. Guests use Google or Email OTP meanwhile.
const PHONE_LOGIN_ENABLED = false;

const GoogleSvg = ({ style }) => (
  <svg style={style} width="24" height="24" viewBox="0 0 48 48">
    <g>
      <circle fill="#fff" cx="24" cy="24" r="24"/>
      <path fill="#4285f4" d="M33.6 24.3c0-.92-.074-1.59-.23-2.28h-9.63v3.6h5.66c-.113.89-.726 2.23-2.077 3.14l-.02.134 3.017 2.35.208.021c1.9-1.77 2.998-4.37 2.998-7.03z"/>
      <path fill="#34a853" d="M23.74 34c2.73 0 5.025-.9 6.7-2.46l-3.202-2.5c-.9.62-2.12 1.056-3.498 1.056-2.683 0-4.96-1.81-5.77-4.18l-.118.01-3.134 2.44-.04.117c1.66 3.26 5.22 5.52 9.063 5.52z"/>
      <path fill="#fbbc05" d="M18.01 26.919a5.97 5.97 0 0 1-.332-1.89c0-.66.12-1.3.31-1.89l-.006-.126-3.168-2.47-.104.05a9.957 9.957 0 0 0-.988 4.43c0 1.62.39 3.15 1.07 4.48l3.24-2.593z"/>
      <path fill="#ea4335" d="M23.74 18.777c1.9 0 3.18.81 3.91 1.49l2.853-2.82c-1.73-1.6-4.07-2.59-6.763-2.59-3.607 0-6.81 2.075-8.053 5.08l3.23 2.504c.45-1.38 1.75-3.662 5.823-3.662z"/>
      <path fill="none" d="M0 0h48v48H0z"/>
    </g>
  </svg>
);

// ─── Telegram Login Widget (EV-818) ──────────────────────────────────────────
// Renders Telegram's official login button (their iframe — the OAuth only works
// from their widget, not a custom button). Webview-friendly: works inside Zalo/FB/
// LINE in-app browsers where Google OAuth is blocked (EV-850). Renders NOTHING
// unless the backend reports a configured bot (TELEGRAM_BOT_TOKEN + _BOT_USERNAME
// set + BotFather /setdomain done). On success it POSTs the signed payload to
// /api/auth/telegram/complete, stores the JWT, and reloads — same as Google.
function TelegramLoginButton() {
  const { t } = useTranslation();
  const containerRef = React.useRef(null);
  const [cfg, setCfg] = useState(null);

  useEffect(() => {
    let alive = true;
    const apiBaseUrl = getApiBaseUrl();
    fetch(`${apiBaseUrl}/auth/telegram/config`)
      .then((r) => r.json())
      .then((d) => { if (alive) setCfg(d); })
      .catch(() => { if (alive) setCfg({ enabled: false }); });
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    if (!cfg?.enabled || !cfg?.botUsername || !containerRef.current) return;

    // Global callback the Telegram widget invokes after a successful auth.
    window.onTelegramAuth = async (user) => {
      try {
        const apiBaseUrl = getApiBaseUrl();
        [
          'venue_app_token', 'token', 'user_email', 'userEmail', 'user_name', 'userName',
          'user_phone', 'userPhone', 'user_id', 'userId', 'user_role', 'userRole',
          'user_region', 'userRegion', 'user_login_method', 'loginMethod', 'cachedUser',
          'cachedUserTokenHash',
        ].forEach((k) => localStorage.removeItem(k));

        const resp = await fetch(`${apiBaseUrl}/auth/telegram/complete`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'x-region': REGION_CONFIG.region },
          credentials: 'include',
          body: JSON.stringify(user),
        });
        const data = await resp.json();
        if (!resp.ok || !data.success) throw new Error(data.message || 'Telegram login failed');

        localStorage.setItem('venue_app_token', data.token);
        localStorage.setItem('token', data.token);
        toast.success(t('auth.welcomeUser', { name: data.name || t('auth.friendFallback') }), { icon: '✅', duration: 3000 });

        const postLoginRedirect = sessionStorage.getItem('post_login_redirect');
        sessionStorage.removeItem('post_login_redirect');
        const target = postLoginRedirect && postLoginRedirect.startsWith('/') ? postLoginRedirect : '/';
        setTimeout(() => { window.location.href = target; }, 800);
      } catch (e) {
        console.error('❌ [LoginForm] Telegram login error:', e);
        toast.error(e.message || t('auth.telegramLoginFailed'));
      }
    };

    // Inject Telegram's official widget script (renders their branded button).
    const script = document.createElement('script');
    script.src = 'https://telegram.org/js/telegram-widget.js?22';
    script.async = true;
    script.setAttribute('data-telegram-login', cfg.botUsername);
    script.setAttribute('data-size', 'large');
    script.setAttribute('data-userpic', 'false');
    script.setAttribute('data-radius', '8');
    script.setAttribute('data-request-access', 'write');
    script.setAttribute('data-onauth', 'onTelegramAuth(user)');
    containerRef.current.innerHTML = '';
    containerRef.current.appendChild(script);

    return () => { try { delete window.onTelegramAuth; } catch (e) { /* noop */ } };
  }, [cfg]);

  if (!cfg?.enabled) return null;

  return (
    <div style={{ width: '100%', display: 'flex', justifyContent: 'center', marginBottom: 18 }}>
      <div ref={containerRef} />
    </div>
  );
}

// ─── LINE in-app browser: open in external browser ───────────────────────────
// LINE's browser blocks web OAuth — users must open the page in Chrome/Safari.
// On Android we can try an intent:// deep-link; on iOS we show instructions.

function LineBrowserGate() {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const url = window.location.href;

  // Android: intent:// deep-link to open in Chrome
  const openInChrome = () => {
    const intentUrl = url.replace(/^https?:\/\//, '')
      .replace(/^/, 'intent://')
      + '#Intent;scheme=https;action=android.intent.action.VIEW;end';
    window.location.href = intentUrl;
  };

  const copyLink = () => {
    navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }).catch(() => {
      // fallback — select text
      const el = document.createElement('textarea');
      el.value = url;
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'linear-gradient(135deg, #fbbf24 0%, #f97316 50%, #fbbf24 100%)',
      fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
      padding: 24,
    }}>
      <div style={{
        background: 'white', borderRadius: 20, padding: 32,
        maxWidth: 360, width: '100%',
        boxShadow: '0 25px 50px rgba(0,0,0,0.25)',
        textAlign: 'center',
      }}>
        <div style={{ fontSize: 52, marginBottom: 12 }}>📱</div>
        <h2 style={{ fontSize: 20, fontWeight: 700, color: '#111827', margin: '0 0 10px' }}>
          {t('auth.openInBrowserTitle')}
        </h2>
        <p style={{ fontSize: 14, color: '#6b7280', lineHeight: 1.6, margin: '0 0 24px' }}>
          {t('auth.lineBrowserGateBody1')}<br />
          {t('auth.lineBrowserGateBody2')} <strong>Chrome</strong> {t('auth.lineBrowserGateBody3')} <strong>Safari</strong>.
        </p>

        {/* Android: try to open in Chrome automatically */}
        <button
          onClick={openInChrome}
          style={{
            display: 'block', width: '100%', padding: '13px 0',
            background: '#06C755', color: 'white', border: 'none',
            borderRadius: 10, fontSize: 15, fontWeight: 700,
            cursor: 'pointer', marginBottom: 12,
          }}
        >
          🌐 {t('auth.openInChromeAndroid')}
        </button>

        {/* Copy link fallback (iOS + all) */}
        <button
          onClick={copyLink}
          style={{
            display: 'block', width: '100%', padding: '13px 0',
            background: copied ? '#dcfce7' : '#f3f4f6',
            color: copied ? '#166534' : '#374151',
            border: 'none', borderRadius: 10, fontSize: 15, fontWeight: 600,
            cursor: 'pointer', transition: 'background 0.2s',
          }}
        >
          {copied ? `✅ ${t('auth.copiedPasteHint')}` : `📋 ${t('auth.copyLinkShort')}`}
        </button>

        <p style={{ fontSize: 12, color: '#9ca3af', marginTop: 20, lineHeight: 1.5 }}>
          {t('auth.lineBrowserHintIphone')}<br />
          {t('auth.lineBrowserHintAndroid')}
        </p>
      </div>
    </div>
  );
}

// ─── LIFF auto-login handler ──────────────────────────────────────────────────
// When LIFF redirects back after auth, this exchanges the LIFF token for a JWT.

function useLiffAutoLogin() {
  const [liffState, setLiffState] = useState('idle'); // idle | loading | done | error

  useEffect(() => {
    if (!isInLineBrowser() || !getLiffId()) return;

    setLiffState('loading');
    console.log('[LIFF] Starting auto-login flow, URL:', window.location.href);
    initLiff().then(async (liffInstance) => {
      if (!liffInstance) {
        console.warn('[LIFF] init returned null — SDK failed to load');
        setLiffState('error');
        return;
      }

      console.log('[LIFF] SDK initialized, isLoggedIn:', isLiffLoggedIn());

      if (!isLiffLoggedIn()) {
        // Not logged in yet — trigger LIFF login.
        // Always redirect to /login so the auth code lands here where
        // useLiffAutoLogin can process it (not on / which lacks this hook).
        console.log('[LIFF] Not logged in, triggering liff.login → /login');
        liffLogin(window.location.origin + '/login');
        return; // page will reload after LIFF auth
      }

      // Already logged in via LIFF — exchange token
      const accessToken = getLiffAccessToken();
      if (!accessToken) {
        console.warn('[LIFF] Logged in but no access token available');
        setLiffState('error');
        return;
      }

      console.log('[LIFF] Got access token, exchanging for JWT...');
      try {
        // LIFF exchange is at /auth/liff/exchange (NOT under /api)
        const apiBaseUrl = getApiBaseUrl();
        const backendRoot = apiBaseUrl.replace(/\/api$/, '');
        const exchangeUrl = `${backendRoot}/auth/liff/exchange`;
        console.log('[LIFF] Exchange URL:', exchangeUrl);
        const res = await fetch(exchangeUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ liffToken: accessToken }),
        });
        const data = await res.json();
        console.log('[LIFF] Exchange response:', { success: data.success, hasToken: !!data.token });

        if (data.success && data.token) {
          localStorage.setItem('venueapp_token', data.token);
          localStorage.setItem('venue_app_token', data.token);
          localStorage.setItem('token', data.token);

          // Handle invite token stored before login
          const pendingInvite = localStorage.getItem('pending_invite_token')
            || sessionStorage.getItem('pending_invite_token');
          localStorage.removeItem('pending_invite_token');
          sessionStorage.removeItem('pending_invite_token');

          // v68.89: honor sessionStorage 'post_login_redirect' so e.g. the
          // review composer can bring the user back to the same venue page
          // after they click "Log in to post review".
          const postLoginRedirect = sessionStorage.getItem('post_login_redirect');
          sessionStorage.removeItem('post_login_redirect');

          if (pendingInvite) {
            window.location.href = '/join?token=' + encodeURIComponent(pendingInvite);
          } else if (postLoginRedirect && postLoginRedirect.startsWith('/')) {
            window.location.href = postLoginRedirect;
          } else {
            window.location.href = '/';
          }
        } else {
          setLiffState('error');
        }
      } catch {
        setLiffState('error');
      }
    });
  }, []);

  return liffState;
}

const LoginForm = () => {
  const { t } = useTranslation();
  const [loginMethod, setLoginMethod] = useState('email'); // 'email' or 'phone'
  const { user } = useAuth();
  const liffState = useLiffAutoLogin();

  // v68.409 — Google GIS button vanishes when accounts.google.com/gsi/client is
  // blocked (ad-blockers, restricted networks, and in-app webviews like Zalo/
  // Facebook/LINE — very common in VN). <GoogleLogin> then renders NOTHING and the
  // user has no way to sign in with Google. Detect that and fall back to the
  // server OAuth redirect flow (same approach as the LINE button, which works
  // everywhere). If GIS loads within 3s we keep the native widget.
  const [gisFailed, setGisFailed] = useState(false);
  useEffect(() => {
    if (window.google?.accounts?.id) return; // already loaded
    const timer = setTimeout(() => {
      if (!window.google?.accounts?.id) setGisFailed(true);
    }, 3000);
    return () => clearTimeout(timer);
  }, []);

  // Redirect-based Google login — works in any browser/webview (no GIS script).
  const handleGoogleRedirect = () => {
    const backendRoot = getApiBaseUrl().replace(/\/api$/, '');
    const params = new URLSearchParams({ region: REGION_CONFIG.region || 'vn' });
    const hlsrc = localStorage.getItem('hlsrc');
    if (hlsrc) params.set('hlsrc', hlsrc);
    // Preserve a pending invite token across the OAuth round-trip (as LINE does)
    const qp = new URLSearchParams(window.location.search);
    const inviteToken = qp.get('invite_token') || qp.get('token');
    if (inviteToken) sessionStorage.setItem('pending_invite_token', inviteToken);
    window.location.href = `${backendRoot}/api/auth/google?${params.toString()}`;
  };

  // If inside LINE browser: either LIFF is handling login or we show the gate
  if (isInLineBrowser()) {
    if (getLiffId()) {
      // LIFF configured — show spinner while LIFF handles it
      if (liffState === 'loading' || liffState === 'idle') {
        return (
          <div style={{
            minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: 'linear-gradient(135deg, #fbbf24 0%, #f97316 100%)',
          }}>
            <div style={{ textAlign: 'center', color: 'white' }}>
              <div style={{
                width: 44, height: 44, border: '4px solid rgba(255,255,255,0.3)',
                borderTop: '4px solid white', borderRadius: '50%',
                animation: 'spin 1s linear infinite', margin: '0 auto 16px',
              }} />
              <div style={{ fontSize: 15, fontWeight: 600 }}>{t('auth.signingInWithLine')}</div>
              <style>{`@keyframes spin{0%{transform:rotate(0deg);}100%{transform:rotate(360deg);}}`}</style>
            </div>
          </div>
        );
      }
      if (liffState === 'error') {
        // LIFF failed — fall through to "open in browser" gate
        return <LineBrowserGate />;
      }
    } else {
      // No LIFF ID — show "open in browser" instructions
      return <LineBrowserGate />;
    }
  }

  // If user is already authenticated, redirect to home
  if (user) {
    return <Navigate to="/" replace />;
  }

  // ✅ PHASE C: Handle Google credential response (GIS Style A - ID token)
  const handleGoogleSuccess = async (credentialResponse) => {
    console.log('✅ [LoginForm] Google login success, credential received');
    
    try {
      // ✅ CRITICAL: Verify credential exists
      if (!credentialResponse?.credential) {
        console.error('❌ [LoginForm] Missing GIS credential', credentialResponse);
        throw new Error('Google credential missing from GIS callback');
      }

      console.log('📦 [LoginForm] Credential received (length):', credentialResponse.credential.length);
      
      // ✅ STEP 1: Clear ALL stale auth data BEFORE login
      console.log('🧹 [LoginForm] Clearing stale auth data before Google login');
      const keysToRemove = [
        'venue_app_token', 'token',
        'user_email', 'userEmail',
        'user_name', 'userName',
        'user_phone', 'userPhone',
        'user_id', 'userId',
        'user_role', 'userRole',
        'user_region', 'userRegion',
        'user_login_method', 'loginMethod',
        'cachedUser', 'cachedUserTokenHash'
      ];
      keysToRemove.forEach(key => localStorage.removeItem(key));
      
      const apiBaseUrl = getApiBaseUrl();
      const hlsrc = localStorage.getItem('hlsrc');
      
      // Parse hlsrc if it exists
      let hlSrcParsed = null;
      if (hlsrc) {
        try {
          hlSrcParsed = JSON.parse(hlsrc);
        } catch (e) {
          console.warn('Failed to parse hlsrc:', e);
        }
      }

      const requestBody = {
        credential: credentialResponse.credential, // ✅ MUST be present
        region: REGION_CONFIG.region || 'th',
        ...(hlSrcParsed && { hl_src: hlSrcParsed })
      };

      console.log('📤 [LoginForm] Sending request to backend:', {
        url: `${apiBaseUrl}/auth/google/token`,
        body: { ...requestBody, credential: 'REDACTED' }
      });
      
      // ✅ STEP 2: Send credential to backend for verification
      const response = await fetch(`${apiBaseUrl}/auth/google/token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-region': REGION_CONFIG.region
        },
        credentials: 'include', // Important for cookies
        body: JSON.stringify(requestBody)
      });

      const data = await response.json();

      if (!response.ok) {
        console.error('❌ [LoginForm] Backend error:', response.status, data);
        throw new Error(data.message || 'Google authentication failed');
      }

      console.log('✅ [LoginForm] Backend verified Google token');

      // ✅ STEP 3: Store ONLY token (no user data in localStorage)
      localStorage.setItem('venue_app_token', data.token);
      localStorage.setItem('token', data.token);

      toast.success(t('auth.welcomeUser', { name: data.user.name }), {
        duration: 3000,
        icon: '✅'
      });

      // ✅ STEP 4: Reload to let AuthContext fetch user from /api/users/me
      console.log('🔄 [LoginForm] Reloading to initialize auth...');
      // v68.89: honor sessionStorage 'post_login_redirect' (set by callers
      // like the review composer) so users land back where they came from.
      const postLoginRedirect = sessionStorage.getItem('post_login_redirect');
      sessionStorage.removeItem('post_login_redirect');
      const target = postLoginRedirect && postLoginRedirect.startsWith('/') ? postLoginRedirect : '/';
      setTimeout(() => { window.location.href = target; }, 1000);

    } catch (error) {
      console.error('❌ [LoginForm] Google login error:', error);
      toast.error(error.message || t('auth.googleLoginFailed'));
    }
  };

  const handleGoogleError = () => {
    console.error('❌ Google OAuth error');
    toast.error(t('auth.googleLoginFailedRetry'));
  };

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'linear-gradient(135deg, #fbbf24 0%, #f97316 50%, #fbbf24 100%)',
      fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
    }}>
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.3 }}
        style={{ maxWidth: 448, width: '100%', margin: '0 16px' }}
      >
        <div style={{
          backgroundColor: 'white',
          borderRadius: 16,
          boxShadow: '0 25px 50px rgba(0,0,0,0.25)',
          overflow: 'hidden'
        }}>
          <div style={{
            padding: '32px 32px 24px',
            background: 'linear-gradient(to right, #f59e0b, #ea580c)',
            color: 'white'
          }}>
            <motion.div
              initial={{ y: -20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.2 }}>
              <h2 style={{
                fontSize: 30,
                fontWeight: 'bold',
                textAlign: 'center',
                margin: 0
              }}>{t('auth.welcomeBackHeading')}</h2>
              <p style={{
                textAlign: 'center',
                color: '#fef3c7',
                margin: '8px 0 0',
                fontSize: 14
              }}>
                {t('auth.discoverIn', { region: REGION_CONFIG.displayName })}
              </p>
            </motion.div>
          </div>

          {/* Google Login Button */}
          <div style={{ padding: '24px 32px 0' }}>
            {/* v68.471 — unified full-width Google button matching LINE/Zalo, on the
                server OAuth REDIRECT flow (/api/auth/google) which is now Neon-backed
                (passport strategy ported off Mongo) + has a vn frontend URL. Works in
                Zalo/FB/LINE webviews too (GIS-independent). */}
            <div style={{
              width: '100%',
              display: 'flex',
              justifyContent: 'center',
              marginBottom: 18
            }}>
              <button
                type="button"
                onClick={handleGoogleRedirect}
                style={{
                  width: '100%', maxWidth: 400, padding: '11px 16px',
                  backgroundColor: 'white', color: '#3c4043',
                  border: '1px solid #dadce0', borderRadius: 6,
                  fontSize: 15, fontWeight: 600, cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
                  transition: 'background-color 0.2s, box-shadow 0.2s',
                  // Google brand = white button; lift it off the white card so it
                  // reads as a banner like the LINE/Zalo bars (matches GIS widget).
                  boxShadow: '0 1px 3px rgba(60,64,67,0.18), 0 1px 2px rgba(60,64,67,0.10)',
                }}
                onMouseOver={(e) => { e.currentTarget.style.backgroundColor = '#f8f9fa'; e.currentTarget.style.boxShadow = '0 2px 6px rgba(60,64,67,0.22)'; }}
                onMouseOut={(e) => { e.currentTarget.style.backgroundColor = 'white'; e.currentTarget.style.boxShadow = '0 1px 3px rgba(60,64,67,0.18), 0 1px 2px rgba(60,64,67,0.10)'; }}
              >
                <GoogleSvg style={{ width: 20, height: 20 }} />
                {t('auth.continueWithGoogle')}
              </button>
            </div>

            {/* Zalo Login Button (EV-1243) — placed directly under Google per Patrick 2026-07-14 */}
            {ZALO_LOGIN_ENABLED && (
            <div style={{
              width: '100%',
              display: 'flex',
              justifyContent: 'center',
              marginBottom: 18
            }}>
              <button
                type="button"
                onClick={() => {
                  const apiBaseUrl = getApiBaseUrl();
                  const backendRoot = apiBaseUrl.replace(/\/api$/, '');
                  const params = new URLSearchParams(window.location.search);
                  const inviteToken = params.get('invite_token') || params.get('token');
                  if (inviteToken) sessionStorage.setItem('pending_invite_token', inviteToken);
                  window.location.href = `${backendRoot}/auth/zalo`;
                }}
                style={{
                  width: '100%',
                  maxWidth: 400,
                  padding: '11px 16px',
                  backgroundColor: '#0068FF',
                  color: 'white',
                  border: 'none',
                  borderRadius: 6,
                  fontSize: 15,
                  fontWeight: 600,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 10,
                  transition: 'background-color 0.2s'
                }}
                onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#0052cc'}
                onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#0068FF'}
              >
                <svg width="22" height="22" viewBox="0 0 48 48" fill="none">
                  <circle cx="24" cy="24" r="22" fill="rgba(255,255,255,0.2)" />
                  <text x="24" y="32" textAnchor="middle" fill="white" fontSize="22" fontWeight="bold" fontFamily="Arial">Z</text>
                </svg>
                {t('auth.signInWithZalo')}
              </button>
            </div>
            )}

            {/* LINE Login Button */}
            <div style={{
              width: '100%',
              display: 'flex',
              justifyContent: 'center',
              marginBottom: 18
            }}>
              <button
                type="button"
                onClick={() => {
                  const apiBaseUrl = getApiBaseUrl();
                  const backendRoot = apiBaseUrl.replace(/\/api$/, '');
                  // Preserve any pending invite token in sessionStorage so it
                  // survives the OAuth redirect round-trip
                  const params = new URLSearchParams(window.location.search);
                  const inviteToken = params.get('invite_token') || params.get('token');
                  if (inviteToken) sessionStorage.setItem('pending_invite_token', inviteToken);
                  window.location.href = `${backendRoot}/auth/line`;
                }}
                style={{
                  width: '100%',
                  maxWidth: 400,
                  padding: '11px 16px',
                  backgroundColor: '#06C755',
                  color: 'white',
                  border: 'none',
                  borderRadius: 6,
                  fontSize: 15,
                  fontWeight: 600,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 10,
                  transition: 'background-color 0.2s'
                }}
                onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#05b34d'}
                onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#06C755'}
              >
                <svg width="22" height="22" viewBox="0 0 24 24" fill="white">
                  <path d="M12 2C6.48 2 2 5.82 2 10.5c0 3.57 2.74 6.62 6.63 7.73-.09.78-.52 2.84-.55 3.01 0 0-.01.09.04.13.05.03.12.01.12.01.16-.02 1.87-1.23 2.73-1.81.67.1 1.36.15 2.03.15 5.52 0 10-3.82 10-8.5S17.52 2 12 2zm-3.39 11.24H6.54a.56.56 0 0 1-.56-.56V8.51c0-.31.25-.56.56-.56s.56.25.56.56v3.62h1.51c.31 0 .56.25.56.56s-.25.56-.56.56zm1.98-.56a.56.56 0 0 1-1.12 0V8.51a.56.56 0 0 1 1.12 0v4.17zm4.01 0c0 .23-.14.43-.35.52a.56.56 0 0 1-.61-.12l-2.01-2.73v2.33a.56.56 0 0 1-1.12 0V8.51c0-.23.14-.43.35-.52a.56.56 0 0 1 .61.12l2.01 2.73V8.51a.56.56 0 0 1 1.12 0v4.17zm3.19-2.55a.56.56 0 0 1 0 1.12H16.3v1.05h1.49a.56.56 0 0 1 0 1.12H15.74a.56.56 0 0 1-.56-.56V8.51c0-.31.25-.56.56-.56H17.79a.56.56 0 0 1 0 1.12H16.3v1.06h1.49z"/>
                </svg>
                {t('auth.signInWithLine')}
              </button>
            </div>

            {/* Telegram Login (EV-818) — renders only when a bot is configured on the
                backend. Works inside in-app webviews where Google OAuth is blocked. */}
            <TelegramLoginButton />

            <div style={{
              textAlign: 'center',
              color: '#a1a1aa',
              fontWeight: 600,
              fontSize: 14,
              margin: '10px 0'
            }}>
              {t('auth.or')}
            </div>
          </div>

          {/* Email/Phone Toggle — hidden while phone OTP is disabled (email-only) */}
          {PHONE_LOGIN_ENABLED && (
          <div style={{ padding: '0 32px', marginBottom: 16 }}>
            <div style={{
              display: 'flex',
              gap: 8,
              backgroundColor: '#f3f4f6',
              padding: 4,
              borderRadius: 12
            }}>
              <button
                onClick={() => setLoginMethod('email')}
                type="button"
                style={{
                  flex: 1,
                  padding: '10px 0',
                  borderRadius: 10,
                  border: 'none',
                  fontWeight: 600,
                  fontSize: 15,
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  backgroundColor: loginMethod === 'email' ? '#f59e0b' : 'transparent',
                  color: loginMethod === 'email' ? 'white' : '#6b7280'
                }}
              >
                {t('auth.emailTab')}
              </button>
              <button
                onClick={() => setLoginMethod('phone')}
                type="button"
                style={{
                  flex: 1,
                  padding: '10px 0',
                  borderRadius: 10,
                  border: 'none',
                  fontWeight: 600,
                  fontSize: 15,
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  backgroundColor: loginMethod === 'phone' ? '#f59e0b' : 'transparent',
                  color: loginMethod === 'phone' ? 'white' : '#6b7280'
                }}
              >
                {t('auth.phoneTab')}
              </button>
            </div>
          </div>
          )}

          {/* Login Form — phone OTP disabled → always Email OTP */}
          <div style={{ padding: '0 32px 32px' }}>
            {PHONE_LOGIN_ENABLED && loginMethod === 'phone' ? (
              <PhoneOTPLoginForm key="phone-otp-form" />
            ) : (
              <EmailOTPLoginForm key="email-otp-form" />
            )}
          </div>
        </div>
      </motion.div>

      <style>
        {`@keyframes spin {0%{transform:rotate(0deg);}100%{transform:rotate(360deg);}}`}
      </style>
    </div>
  );
};

export default LoginForm;
